import { DateTime } from "luxon";
import { classes } from "polytype";
import type {
  SftpUserCreateRequestData,
  SftpUserListQuery,
  SshUserCreateRequestData,
  SshUserListQuery,
} from "../../access/index.js";
import { SftpUser, SshUser } from "../../access/index.js";
import type {
  AppInstallationCreateRequestData,
  AppInstallationListQuery,
} from "../../app/index.js";
import { AppInstallation } from "../../app/index.js";
import type { HostingTariff } from "../../article/index.js";
import { Article } from "../../article/index.js";
import type {
  BackupCreateRequestData,
  BackupListQuery,
  BackupScheduleCreateRequestData,
  BackupScheduleListQuery,
} from "../../backup/index.js";
import { Backup, BackupSchedule } from "../../backup/index.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
  extractId,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import type {
  ContainerListQuery,
  RegistryListQuery,
  VolumeListQuery,
} from "../../container/index.js";
import { Container, Registry, Volume } from "../../container/index.js";
import { Contract, HostingConfiguration } from "../../contract/index.js";
import type {
  CronjobCreateRequestData,
  CronjobListQuery,
} from "../../cronjob/index.js";
import { Cronjob } from "../../cronjob/index.js";
import { Customer } from "../../customer/index.js";
import type {
  MySqlCreateRequest,
  MySqlListQuery,
  RedisCreateRequestData,
  RedisListQuery,
} from "../../database/index.js";
import { MySql, Redis } from "../../database/index.js";
import type {
  CertificateListQuery,
  DnsZoneListQuery,
  DomainListQuery,
  IngressListQuery,
} from "../../domain/index.js";
import { Certificate, DnsZone, Domain, Ingress } from "../../domain/index.js";
import {
  File,
  type DomFile,
  type FileAccessTokenProvider,
} from "../../file/index.js";
import type {
  DeliveryBoxListQuery,
  ForwardRequestData,
  MailAddressListQuery,
  MailAddressRequestData,
} from "../../mail/index.js";
import { DeliveryBox, MailAddress, MailSettings } from "../../mail/index.js";
import type { ExtensionInstanceListQuery } from "../../marketplace/index.js";
import { ExtensionInstance } from "../../marketplace/index.js";
import { StorageMetrics } from "../../monitoring/StorageMetrics";
import { ProjectUsageMetrics } from "../../monitoring/UsageMetrics/ProjectUsageMetrics";
import type { OrderListQuery } from "../../order/index.js";
import { Order } from "../../order/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { Server } from "../../server/index.js";
import type {
  ProjectInviteCreateRequestData,
  ProjectInviteListQuery,
} from "../ProjectInvite/index.js";
import { ProjectInvite } from "../ProjectInvite/index.js";
import type { ProjectMembershipListQuery } from "../ProjectMembership/index.js";
import { ProjectMembership } from "../ProjectMembership/index.js";
import type { ProjectPermission } from "../projectPermissions.js";
import { projectPermissions } from "../projectPermissions.js";
import { ProjectAvatarAccessTokenProvider } from "./ProjectAvatarAccessTokenProvider.js";
import type {
  ProjectData,
  ProjectDisableReason,
  ProjectListItemData,
  ProjectListQueryModelData,
  ProjectStatus,
} from "./types.js";

export class Project extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData("project", "project");
  public readonly fileAccessTokenProvider: FileAccessTokenProvider;
  public readonly ingresses: IngressListQuery;
  public readonly domains: DomainListQuery;
  public readonly dnsZones: DnsZoneListQuery;
  public readonly appInstallations: AppInstallationListQuery;
  public readonly invites: ProjectInviteListQuery;
  public readonly memberships: ProjectMembershipListQuery;
  public readonly mailAddresses: MailAddressListQuery;
  public readonly deliveryBoxes: DeliveryBoxListQuery;
  public readonly mailSettings: MailSettings;
  public readonly certificates: CertificateListQuery;
  public readonly extensionInstances: ExtensionInstanceListQuery;
  public readonly sshUsers: SshUserListQuery;
  public readonly sftpUsers: SftpUserListQuery;
  public readonly mySqlDatabases: MySqlListQuery;
  public readonly redisDatabases: RedisListQuery;
  public readonly backups: BackupListQuery;
  public readonly backupSchedules: BackupScheduleListQuery;
  public readonly cronjobs: CronjobListQuery;
  public readonly orders: OrderListQuery;
  public readonly containers: ContainerListQuery;
  public readonly volumes: VolumeListQuery;
  public readonly registries: RegistryListQuery;

  public constructor(id: string) {
    super(id);
    this.fileAccessTokenProvider = new ProjectAvatarAccessTokenProvider(this);
    this.ingresses = Ingress.query({
      project: this,
    });
    this.domains = Domain.query({
      project: this,
    });
    this.dnsZones = DnsZone.query(this);
    this.appInstallations = AppInstallation.query({ project: this });
    this.invites = ProjectInvite.query(this);
    this.memberships = ProjectMembership.query(this);
    this.mailAddresses = MailAddress.query(this);
    this.deliveryBoxes = DeliveryBox.query(this);
    this.certificates = Certificate.query({ project: this });
    this.extensionInstances = ExtensionInstance.query({ project: this });
    this.mailSettings = MailSettings.ofId(this.id);
    this.sshUsers = SshUser.query(this);
    this.sftpUsers = SftpUser.query(this);
    this.mySqlDatabases = MySql.query(this);
    this.redisDatabases = Redis.query(this);
    this.backups = Backup.query(this);
    this.backupSchedules = BackupSchedule.query(this);
    this.cronjobs = Cronjob.query(this);
    this.orders = Order.query({ project: this });
    this.containers = Container.query(this);
    this.volumes = Volume.query(this);
    this.registries = Registry.query(this);
  }

  public static findAggregate(projectId?: string) {
    return projectId
      ? { id: projectId, ...Project.aggregateMetaData }
      : undefined;
  }

  public static ofId(id: string) {
    return new Project(id);
  }

  public static ofReference(ref?: string | Project) {
    const id = extractId(ref);
    return id ? Project.ofId(id) : undefined;
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.project.find(id);

    if (data !== undefined) {
      return new ProjectDetailed(data);
    }
  });

  public static get = provideReact(async (id: string | Project) => {
    const project = await this.find(extractId(id));
    assertObjectFound(project, "project", id);
    return project;
  });

  public findDetailed = provideReact(
    () => Project.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ProjectDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Project.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ProjectDetailed>>;

  public static query(query: ProjectListQueryModelData = {}) {
    return new ProjectListQuery(query);
  }

  public static async create(data: { serverId: string; description: string }) {
    const { serverId, description } = data;

    const { id } = await config.behaviors.project.create(serverId, description);
    return new Project(id);
  }

  public getDefaultIngress = provideReact(async () => {
    const ingresses = await this.ingresses.execute();
    const defaultIngress = ingresses.items.find((i) => i.data.isDefault);
    assertObjectFound(defaultIngress, "ingress", this);
    return defaultIngress;
  }, [this.id]);

  public async updateDescription(description: string) {
    await config.behaviors.project.updateDescription(this.id, description);
  }

  public async delete() {
    await config.behaviors.project.delete(this.id);
  }

  public async createMailAddress(data: MailAddressRequestData) {
    return MailAddress.createMailAddress(this, data);
  }

  public async createForward(data: ForwardRequestData) {
    return MailAddress.createForward(this, data);
  }

  public async createDeliveryBox(description: string, password: string) {
    return DeliveryBox.create(this, description, password);
  }

  public async createMySql(data: MySqlCreateRequest) {
    return MySql.create(this, data);
  }

  public async createRedis(data: RedisCreateRequestData) {
    return Redis.create(this, data);
  }

  public async createSshUser(data: SshUserCreateRequestData) {
    return SshUser.create(this, data);
  }

  public async createSftpUser(data: SftpUserCreateRequestData) {
    return SftpUser.create(this, data);
  }

  public async createCronjob(data: CronjobCreateRequestData) {
    return Cronjob.create(this, data);
  }

  public getOwnMembership = provideReact(async () => {
    return await ProjectMembership.getOwn(this);
  }, [this.id]);

  public async inviteMember(data: ProjectInviteCreateRequestData) {
    return ProjectInvite.create(this, data);
  }

  public async createBackup(data: BackupCreateRequestData) {
    return Backup.create(this, data);
  }

  public async createBackupSchedule(data: BackupScheduleCreateRequestData) {
    return BackupSchedule.create(this, data);
  }

  public async installApp(data: AppInstallationCreateRequestData) {
    return AppInstallation.create(this, data);
  }

  public async uploadAvatar(file: DomFile) {
    await File.upload(file, this.fileAccessTokenProvider);
  }

  public getAvatarUploadRules = provideReact(async () => {
    return File.getUploadRules("avatar");
  });

  public async removeAvatar() {
    await config.behaviors.project.removeAvatar(this.id);
  }

  public getContract = provideReact(async () => {
    return await Contract.getByProject(this.id);
  }, [this.id]);

  public getHostingConfiguration = provideReact(async () => {
    const contract = await this.getContract();
    const article = await Article.get(
      contract.baseItemArticles[0]?.articleId ?? "",
    );

    return new HostingConfiguration(article, contract);
  }, [this.id]);

  public async updateTariff(data: {
    tariff: HostingTariff;
    storage: number;
    spec: { vcpu: number; ram: number };
  }) {
    const { tariff, storage, spec } = data;
    const contract = await this.getContract();
    const specs = await Order.getProjectArticleSpecs("project", tariff, spec);

    await Order.createTariffChange({
      tariffChangeType: "projectHosting",
      tariffChangeData: {
        diskspaceInGiB: storage,
        spec: specs,
        contractId: contract.id,
      },
    });
  }

  public findStorageMetrics = provideReact(async () => {
    return await StorageMetrics.find(this.id, "project");
  }, [this.id]);

  public async updateStorageNotificationThreshold(threshold?: number) {
    await config.behaviors.project.updateStorageNotificationThreshold(
      this.id,
      threshold,
    );
  }
}

class ProjectCommon extends classes(
  DataModel<ProjectListItemData | ProjectData>,
  Project,
) {
  public readonly server?: Server;
  public readonly customer: Customer;
  public readonly shortId: string;
  public readonly description: string;
  public readonly disabledAt?: DateTime;
  public readonly enabled: boolean;
  public readonly createdAt: DateTime;
  public readonly avatar?: File;
  public readonly isProSpaceLite: boolean;
  public readonly status: ProjectStatus;
  public readonly disabledReason?: ProjectDisableReason;
  public readonly isAllowedToPlaceOrders: boolean;

  public constructor(data: ProjectListItemData | ProjectData) {
    super([data], [data.id]);
    this.server =
      !data.projectHostingId && data.serverId
        ? Server.ofId(data.serverId)
        : undefined;
    this.customer = Customer.ofId(data.customerId);
    this.shortId = data.shortId;
    this.description = data.description;
    if (data.disabledAt) {
      this.disabledAt = DateTime.fromISO(data.disabledAt);
    }
    this.createdAt = DateTime.fromISO(data.createdAt);
    this.avatar = data.imageRefId ? File.ofId(data.imageRefId) : undefined;
    this.isProSpaceLite = !!data.projectHostingId && !data.serverId;
    this.enabled = data.enabled;
    this.status = data.status;
    this.disabledReason = data.disableReason;
    this.isAllowedToPlaceOrders = data.disableReason !== "suspended";
  }

  public findContract = provideReact(async () => {
    const ownMembership = await this.getOwnMembership();
    if (!ownMembership.inherited) {
      return;
    }
    return await Contract.findByProject(this.id);
  }, [this.id]);

  public hasPermission = provideReact(
    async (permission: ProjectPermission) => {
      const ownMembership = await this.getOwnMembership();

      const inheritedRequired =
        projectPermissions[permission].includes("inheritedOwner");

      if (inheritedRequired) {
        return ownMembership.inherited;
      }

      return projectPermissions[permission].includes(ownMembership.role);
    },
    [this.id],
  );
}

export class ProjectDetailed extends classes(
  ProjectCommon,
  DataModel<ProjectData>,
) {
  public readonly hostname: string;

  public constructor(data: ProjectData) {
    super([data], [data]);
    this.hostname = `ssh.${data.clusterID}.${data.clusterDomain}`;
  }

  public getBaseDirectory(base?: "Home" | "Logs" | "Web") {
    if (!base) {
      return "";
    }

    return this.data.directories[base];
  }

  public findFileSystemDirectories = provideReact(
    async (directory: string) => {
      return await config.behaviors.project.findFileSystemDirectories(
        this.id,
        directory,
      );
    },
    [this.id],
  );

  public getMetrics() {
    return new ProjectUsageMetrics(this);
  }
}

export class ProjectListItem extends classes(
  ProjectCommon,
  DataModel<ProjectListItemData>,
) {
  public constructor(data: ProjectListItemData) {
    super([data], [data]);
  }
}

export class ProjectListQuery extends ListQueryModel<ProjectListQueryModelData> {
  public constructor(query: ProjectListQueryModelData = {}) {
    super(query);
  }

  public refine(query: ProjectListQueryModelData) {
    return new ProjectListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { server, customer, ...query } = this.query;
    const { items, totalCount } = await config.behaviors.project.list({
      ...query,
      serverId: server?.id,
      customerId: customer?.id,
    });

    return new ProjectList(
      this.query,
      items.map((d) => new ProjectListItem(d)),
      totalCount,
    );
  }, [this.queryId, "project"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "project"]);
}

export class ProjectList extends classes(
  ProjectListQuery,
  ListDataModel<ProjectListItem>,
) {
  public constructor(
    query: ProjectListQueryModelData,
    projects: ProjectListItem[],
    totalCount: number,
  ) {
    super([query], [projects, totalCount]);
  }
}
